import { useEffect, useRef, useState } from 'preact/hooks';
import { fetchMediaObjectUrl } from '../lib/api.js';
import { downloadObjectUrl, formatBytes } from '../lib/image.js';
import type { StoredMedia } from '../lib/db.js';
import { t } from '../lib/i18n.js';
import { CloseIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons.js';

/**
 * Full-screen photo viewer: tap a thumbnail to open here, swipe or use the
 * arrow buttons to browse the rest of the same message's photos. Thumbnails
 * are already inline in the message (base64, ADR-0005/0006) so the grid
 * never fetches anything; only opening the full-size view does.
 */

const SWIPE_THRESHOLD_PX = 50;

export function ImageViewer({
  media,
  startIndex,
  accessToken,
  onClose,
}: {
  media: StoredMedia[];
  startIndex: number;
  accessToken: string;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(startIndex);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const urlCache = useRef(new Map<string, string>());
  const touchStartX = useRef<number | null>(null);

  const current = media[index];

  useEffect(() => {
    if (!current) return;
    setLoadError(false);
    const cached = urlCache.current.get(current.id);
    if (cached) {
      setObjectUrl(cached);
      return;
    }
    setObjectUrl(null);
    fetchMediaObjectUrl(accessToken, current.id)
      .then((url) => {
        urlCache.current.set(current.id, url);
        setObjectUrl(url);
      })
      .catch(() => setLoadError(true));
    // Deliberately omits `accessToken` from deps: it's stable per session, and
    // re-fetching on every render (it's a new value only on login) isn't wanted.
  }, [current?.id]);

  // Revoke every cached blob URL once the viewer closes, not per-navigation.
  useEffect(() => {
    const cache = urlCache.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, media.length]);

  function go(delta: number) {
    setIndex((i) => Math.min(Math.max(i + delta, 0), media.length - 1));
  }

  function onTouchStart(e: TouchEvent) {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(e: TouchEvent) {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (dx > SWIPE_THRESHOLD_PX) go(-1);
    else if (dx < -SWIPE_THRESHOLD_PX) go(1);
    touchStartX.current = null;
  }

  if (!current) return null;

  return (
    <div className="viewer" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <button type="button" className="viewer__close" onClick={onClose} aria-label={t('viewer.close')}>
        <CloseIcon />
      </button>

      {objectUrl && (
        <button
          type="button"
          className="viewer__download"
          onClick={() => downloadObjectUrl(objectUrl, current.id, current.mime)}
          aria-label={t('viewer.download')}
        >
          <DownloadIcon />
        </button>
      )}

      {(media.length > 1 || current.bytes) && (
        <span className="viewer__count">
          {media.length > 1 && `${index + 1} / ${media.length}`}
          {media.length > 1 && current.bytes ? ' · ' : ''}
          {current.bytes ? formatBytes(current.bytes) : ''}
        </span>
      )}

      <div className="viewer__stage">
        {index > 0 && (
          <button
            type="button"
            className="viewer__nav viewer__nav--prev"
            onClick={() => go(-1)}
            aria-label={t('viewer.prev')}
          >
            <ChevronLeftIcon />
          </button>
        )}

        {loadError ? (
          <p className="viewer__error">{t('viewer.loadError')}</p>
        ) : objectUrl ? (
          <img src={objectUrl} alt="" className="viewer__image" />
        ) : (
          <p className="viewer__loading">{t('viewer.loading')}</p>
        )}

        {index < media.length - 1 && (
          <button
            type="button"
            className="viewer__nav viewer__nav--next"
            onClick={() => go(1)}
            aria-label={t('viewer.next')}
          >
            <ChevronRightIcon />
          </button>
        )}
      </div>
    </div>
  );
}
